/**
 * ChatPanel - AI chat interface with real LLM backend
 *
 * Uses template questions from config for type-specific suggestions.
 */

import { useState, useRef, useEffect } from 'react'
import type { CompanyData, ChatMessage } from '../../types'
import { sendChatMessage, type ChatContext } from '../../services/api'
import { getSuggestedChatQuestions, type CompanyTypeKey } from '../../config/templateQuestions'

interface ChatPanelProps {
  company: CompanyData | null
  context?: ChatContext
  onSendMessage?: (message: string) => void
}

/**
 * Get company type key from CompanyData for template questions
 */
function getCompanyTypeKey(company: CompanyData | null): CompanyTypeKey {
  if (!company) return 'EQUITY'
  const quoteType = company.quoteType?.toUpperCase()
  if (company.isPrivate || quoteType === 'PRIVATE') return 'PRIVATE'
  if (quoteType === 'ETF') return 'ETF'
  if (quoteType === 'MUTUALFUND') return 'MUTUALFUND'
  return 'EQUITY'
}

export function ChatPanel({ company, context, onSendMessage }: ChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Build chat context from company and provided context
  const buildChatContext = (): ChatContext => {
    return {
      ticker: company?.ticker,
      name: company?.name,
      sector: company?.sector,
      industry: company?.industry,
      marketCap: company?.marketCap,
      ...context, // Override with any provided context (financials, regime, etc.)
    }
  }

  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    const messageToSend = input
    setInput('')
    setIsLoading(true)

    // Notify parent if callback provided
    onSendMessage?.(messageToSend)

    try {
      // Call the real chat API with context
      const chatContext = buildChatContext()
      const result = await sendChatMessage(messageToSend, chatContext)

      let responseContent: string
      let metadata: { confidence?: number } = {}

      if (result.error) {
        responseContent = `I encountered an error processing your request: ${result.error}. Please try again.`
      } else if (result.data) {
        responseContent = result.data.response
        metadata = {
          confidence: result.data.confidence,
        }
      } else {
        responseContent = 'I received an empty response. Please try again.'
      }

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: responseContent,
        timestamp: new Date(),
        metadata,
      }
      setMessages((prev) => [...prev, assistantMessage])
    } catch (error) {
      console.error('Chat error:', error)
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I encountered an unexpected error. Please try again later.',
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleSuggestedQuestion = (question: string) => {
    setInput(question)
  }

  if (!company) {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>Select a company to start a conversation</p>
      </div>
    )
  }

  // Detect company type and get appropriate suggested questions
  const companyTypeKey = getCompanyTypeKey(company)
  const isPrivate = companyTypeKey === 'PRIVATE'
  const isETF = companyTypeKey === 'ETF'
  const isMutualFund = companyTypeKey === 'MUTUALFUND'
  const questionsToShow = getSuggestedChatQuestions(companyTypeKey)

  return (
    <div className="flex flex-col h-full">
      {/* Type-Specific Banner */}
      {isPrivate && (
        <div className="bg-term-info/20 border border-term-info rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">🔒</span>
            <div>
              <span className="text-term-info font-semibold">Private Company Research Mode</span>
              <span className="text-term-textDim text-sm ml-2">
                AI will focus on public information, industry research, and alternative data sources.
              </span>
            </div>
          </div>
        </div>
      )}
      {isETF && (
        <div className="bg-term-success/20 border border-term-success rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">📊</span>
            <div>
              <span className="text-term-success font-semibold">ETF Analysis Mode</span>
              <span className="text-term-textDim text-sm ml-2">
                Questions focus on holdings, tracking error, expenses, and benchmark comparison.
              </span>
            </div>
          </div>
        </div>
      )}
      {isMutualFund && (
        <div className="bg-term-warning/20 border border-term-warning rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">💼</span>
            <div>
              <span className="text-term-warning font-semibold">Mutual Fund Analysis Mode</span>
              <span className="text-term-textDim text-sm ml-2">
                Questions focus on manager track record, style analysis, fees, and performance.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-term-text">
          AI Assistant - {company.ticker}
          {isPrivate && <span className="ml-2 px-2 py-0.5 bg-term-info/30 text-term-info text-xs rounded font-bold">PRIVATE</span>}
          {isETF && <span className="ml-2 px-2 py-0.5 bg-term-success/30 text-term-success text-xs rounded font-bold">ETF</span>}
          {isMutualFund && <span className="ml-2 px-2 py-0.5 bg-term-warning/30 text-term-warning text-xs rounded font-bold">MUTUAL FUND</span>}
        </h2>
        <span className="term-badge term-badge-info">
          Context: {company.name}
        </span>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto mb-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-term-textDim mb-4">
              {isPrivate && `Research ${company.name} using public information, industry data, and alternative sources.`}
              {isETF && `Analyze ${company.ticker} ETF holdings, tracking, costs, and alternatives.`}
              {isMutualFund && `Analyze ${company.ticker} fund manager, performance, fees, and style.`}
              {!isPrivate && !isETF && !isMutualFund && `Ask questions about ${company.ticker}'s financials, FCF predictions, or M&A potential.`}
            </p>
            <div className="space-y-2">
              <p className="text-term-textDim text-sm">
                {isPrivate ? 'Suggested research questions:' :
                 isETF ? 'Suggested ETF analysis questions:' :
                 isMutualFund ? 'Suggested fund analysis questions:' :
                 'Suggested questions:'}
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {questionsToShow.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestedQuestion(q)}
                    className="term-button text-xs"
                  >
                    [{i + 1}] {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))
        )}
        {isLoading && (
          <div className="flex items-center gap-2 text-term-textDim">
            <span className="animate-pulse">...</span>
            <span>Analyzing with AI models...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question about the company..."
          className="term-input flex-1"
          disabled={isLoading}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          className="term-button term-button-primary disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </div>
  )
}

interface MessageBubbleProps {
  message: ChatMessage
}

function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user'
  const metadata = message.metadata as { confidence?: number } | undefined

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] p-3 rounded ${
          isUser
            ? 'bg-term-primary text-white'
            : 'bg-term-bgLight text-term-text'
        }`}
      >
        <div className="text-xs opacity-70 mb-1">
          {isUser ? 'You' : 'Assistant'} |{' '}
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="whitespace-pre-wrap">{message.content}</div>
        {metadata?.confidence !== undefined && metadata.confidence > 0 && (
          <div className="text-xs opacity-50 mt-2">
            Confidence: {(metadata.confidence * 100).toFixed(0)}%
          </div>
        )}
      </div>
    </div>
  )
}
